import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { Observable, catchError, map, of } from 'rxjs'
import { Injectable, inject } from '@angular/core'

import { ConfigDefaults, ConfigResponse, ConfigVersion, ScorecardParams, SimulationPopulation, SimulationRequest, SimulationResult } from './risk-scorecard-config.interface'
import { environment } from '@env/environment'

const CONNECTION_ERROR = 'Hubo un error de conexión'

export interface ConfigDraftBody {
  name: string
  description?: string
  changeReason: string
  params: ScorecardParams
  derivedFromId?: string
}

/**
 * Configuración de la calculadora de riesgo (plan v4.2 §7, E2).
 *
 *   GET    /risk-scorecard-config                · versiones (sin parámetros)
 *   GET    /risk-scorecard-config/active         · la activa con sus parámetros
 *   GET    /risk-scorecard-config/defaults       · semilla y catálogo de rótulos y unidades
 *   GET    /risk-scorecard-config/:id            · una versión con su historial de activaciones
 *   POST   /risk-scorecard-config                · crear un borrador (o duplicar con `derivedFromId`)
 *   PUT    /risk-scorecard-config/:id            · editar un borrador
 *   POST   /risk-scorecard-config/:id/activate   · activar o volver a una archivada, con motivo
 *   DELETE /risk-scorecard-config/:id            · borrar un borrador (solo Admin)
 *   POST   /risk-scorecard-config/simulate       · simular una versión (o lo que se edita) contra la activa
 *   POST   /risk-scorecard-config/simulate/new-clients · lo mismo, sobre los clientes nuevos con preoferta (v4.3 §9.4 y §2b)
 *
 * Ningún método lanza: el error viaja en la respuesta con `code` y la lista `errors` que manda la API cuando los
 * parámetros no pasan la validación.
 */
@Injectable({ providedIn: 'root' })
export class ApiRiskScorecardConfigService {
  private readonly http = inject(HttpClient)
  private readonly base = `${environment.apiUrl}risk-scorecard-config`

  list(): Observable<ConfigResponse<ConfigVersion[]>> {
    return this.wrap(this.http.get(this.base), 'No pudimos cargar las versiones.')
  }

  active(): Observable<ConfigResponse<ConfigVersion | null>> {
    return this.wrap(this.http.get(`${this.base}/active`), 'No pudimos cargar la versión activa.')
  }

  defaults(): Observable<ConfigResponse<ConfigDefaults>> {
    return this.wrap(this.http.get(`${this.base}/defaults`), 'No pudimos cargar los valores de referencia.')
  }

  get(id: string): Observable<ConfigResponse<ConfigVersion>> {
    return this.wrap(this.http.get(`${this.base}/${id}`), 'No pudimos cargar la versión.')
  }

  create(body: ConfigDraftBody): Observable<ConfigResponse<ConfigVersion>> {
    return this.wrap(this.http.post(this.base, body), 'No se pudo guardar el borrador.')
  }

  update(id: string, body: Partial<ConfigDraftBody>): Observable<ConfigResponse<ConfigVersion>> {
    return this.wrap(this.http.put(`${this.base}/${id}`, body), 'No se pudo guardar el borrador.')
  }

  activate(id: string, reason: string): Observable<ConfigResponse<ConfigVersion>> {
    return this.wrap(this.http.post(`${this.base}/${id}/activate`, { reason }), 'No se pudo activar la versión.')
  }

  remove(id: string): Observable<ConfigResponse<{ deleted: boolean; version: number }>> {
    return this.wrap(this.http.delete(`${this.base}/${id}`), 'No se pudo borrar el borrador.')
  }

  simulate(body: SimulationRequest, population: SimulationPopulation = 'clients'): Observable<ConfigResponse<SimulationResult>> {
    const path = population === 'new_clients' ? 'simulate/new-clients' : 'simulate'
    return this.wrap(this.http.post(`${this.base}/${path}`, body), 'No se pudo simular la versión.')
  }

  private wrap<T>(source: Observable<unknown>, fallback: string): Observable<ConfigResponse<T>> {
    return source.pipe(
      map((r: any) => ({ error: !!r?.error, msg: r?.msg ?? '', data: (r?.data ?? null) as T | null, status: 200 })),
      catchError((e: HttpErrorResponse) => {
        const status = e?.status ?? 0
        if (status === 0) return of({ error: true, msg: CONNECTION_ERROR, data: null, status, code: 'NETWORK' })
        const body = e?.error
        return of({
          error: true,
          msg: body?.msg ?? fallback,
          data: null,
          status,
          code: body?.code ?? (status === 403 ? 'FORBIDDEN' : undefined),
          errors: Array.isArray(body?.errors) ? body.errors : undefined,
        })
      }),
    )
  }
}
